import React, { useState, useEffect } from "react";
import {
    Phone,
    MessageSquare,
    Brain,
    Mic,
    FileText,
    User,
    PhoneCall,
    PhoneOff,
    Clock,
    Globe,
    Waves,
    Radio,
} from "lucide-react";
import { toast } from "react-hot-toast";
import "./IVRInitializeCall.css";
import AuthService from "../../services/Auth.service";
import ApiService from "../../services/Api.service";
import ToggleSwitch from "../../components/ToggelSwitch/ToggelSwitch";

export default function IVRInitializeCall() {
    const [formData, setFormData] = useState({
        dialer: "",
        speechToText: "",
        llm: "",
        textToSpeech: "",
        script: "",
        streaming: "false",
        // Call transfer config
        call_transfer: false,
        call_transfer_number: "",
        call_transfer_threshold: "0",
        customerName: "",
        customerNumber: "",
    });
    const [ivrDropdowns, setIvrDropdowns] = useState({
        stt_options: [],
        llm_options: [],
        tts_options: [],
        dialer_options: [],
        script_options: [],
    });
    const [callStatus, setCallStatus] = useState("idle");
    const [duration, setDuration] = useState(0);
    const [languageMode, setLanguageMode] = useState("default"); // default | other
    const [selectedLanguage, setSelectedLanguage] = useState("");
    const [languageOptions, setLanguageOptions] = useState({})
    // Fetch dropdown data from backend
    const fetchDropdownDetail = async (selectedLanguage) => {
        try {
            const payload = {
                language: selectedLanguage
            }
            const ivrOptionsRes = await ApiService.getIvrOptions(payload);
            if (ivrOptionsRes.status === 200) {
                setIvrDropdowns(ivrOptionsRes.data);
            }
        } catch (error) {
            console.error("Error fetching IVR options:", error);
            toast.error("Failed to load IVR configuration options");
        }
    };
    // Fetch dropdown data from backend
    const fetchLanguageOptions = async () => {
        try {
            const languageRes = await ApiService.getLanguageOptions();
            if (languageRes.status === 200) {
                setLanguageOptions(languageRes.data);

                const defaultLang =
                    Array.isArray(languageRes.data?.default_language) && languageRes.data.default_language.length > 0
                        ? languageRes.data.default_language[0].value
                        : "";
                setSelectedLanguage(defaultLang);  // This will trigger your useEffect that fetches dropdown
            }
        } catch (error) {
            toast.error("Failed to load IVR configuration options");
        }
    };


    useEffect(() => {
        fetchLanguageOptions()
    }, []);

    useEffect(() => {
        if (selectedLanguage) {
            fetchDropdownDetail(selectedLanguage);
        }
    }, [selectedLanguage]);

    // Call duration timer
    useEffect(() => {
        if (callStatus === "connected") {
            const interval = setInterval(() => setDuration((prev) => prev + 1), 1000);
            return () => clearInterval(interval);
        } else {
            setDuration(0);
        }
    }, [callStatus]);

    // Submit handler
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.customerName || !formData.customerNumber) {
            toast.error("Please enter customer name and number");
            return;
        }

        if (
            !formData.dialer ||
            !formData.speechToText ||
            !formData.llm ||
            !formData.textToSpeech ||
            !formData.script
        ) {
            toast.error("Please fill in all configuration fields");
            return;
        }

        try {
            const payload = {
                dialer: formData.dialer,
                language: selectedLanguage,
                stt_type: formData.speechToText,
                llm_type: formData.llm,
                tts_type: formData.textToSpeech,
                customer_name: formData.customerName,
                customer_number: formData.customerNumber,
                script: formData.script,
                streaming: formData.streaming === "true",
                call_transfer: Boolean(formData.call_transfer),
                call_transfer_number: formData.call_transfer ? formData.call_transfer_number : "",
                call_transfer_threshold: Number(formData.call_transfer_threshold || 0),
            };


            const res = await AuthService.makeTestCall(payload);

            if (res?.status === 200 && res.data) {
                toast.success("Call connected successfully!");
                setCallStatus("connected");
                setDuration(0);
            } else {
                toast.error("Failed to connect call");
                setCallStatus("ended");
            }
        } catch (error) {
            console.error("Error while making test call:", error);
            toast.error("Error while making test call");
            setCallStatus("ended");
        }
    };

    const handleReset = () => {
        setFormData({
            dialer: "",
            speechToText: "",
            llm: "",
            textToSpeech: "",
            script: "",
            streaming: "",     //  NEW FIELD
            customerName: "",
            customerNumber: "",
        });
        toast.success("Form reset");
    };


    const handleEndCall = () => {
        setCallStatus("ended");
        setTimeout(() => setCallStatus("idle"), 2000);
    };

    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, "0")}:${secs
            .toString()
            .padStart(2, "0")}`;
    };

    const getStatusDisplay = () => {
        switch (callStatus) {
            case "connected":
                return { text: "Connected", color: "#22c55e", icon: Phone };
            case "ended":
                return { text: "Call Ended", color: "#64748b", icon: PhoneOff };
            default:
                return null;
        }
    };

    const statusDisplay = getStatusDisplay();

    return (
        <div className="ivr-container">
            <div className="ivr-grid">
                {/* LEFT PANEL */}
                <div className="ivr-left">
                    <div className="ivr-card">
                        <div className="ivr-card-header">
                            <p>Set up your IVR call parameters and customer details</p>
                        </div>

                        <form onSubmit={handleSubmit} className="ivr-form">
                            {/* DIALER */}
                            <div className="ivr-field">
                                <label>
                                    <Phone size={16} /> Dialer
                                </label>
                                <select
                                    value={formData.dialer}
                                    onChange={(e) =>
                                        setFormData({ ...formData, dialer: e.target.value })
                                    }
                                >
                                    <option value="">Select Dialer</option>
                                    {ivrDropdowns?.dialer_options?.map((item) => (
                                        <option key={item.value} value={item.value}>
                                            {item.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {/* LANGUAGE MODE SELECTION */}
                            <div className="ivr-field">
                                <label>
                                    <Globe size={16} style={{ marginRight: "6px", verticalAlign: "middle" }} />
                                    Language Mode</label>

                                <div className="ivr-radio-group">
                                    <label className="ivr-radio">
                                        <input
                                            type="radio"
                                            name="language_mode"
                                            value={languageOptions?.default_language?.[0]?.value}

                                            checked={languageMode === "default"}
                                            onChange={(e) => {
                                                setLanguageMode("default");
                                                setSelectedLanguage(e.target.value)
                                            }}
                                        />
                                        {languageOptions?.default_language?.[0]?.label || "(No default language)"} (Default)
                                    </label>

                                    <label className="ivr-radio">
                                        <input
                                            type="radio"
                                            name="language_mode"
                                            value="other"
                                            checked={languageMode === "other"}
                                            onChange={() => setLanguageMode("other")}
                                        />
                                        Select Other Language
                                    </label>
                                </div>

                            </div>

                            {/* DROPDOWN WHEN OTHER LANGUAGE SELECTED */}
                            {languageMode === "other" && (
                                <div className="ivr-field">
                                    <label>Choose Language</label>
                                    <select
                                        value={selectedLanguage}
                                        onChange={(e) => setSelectedLanguage(e.target.value)}
                                    >
                                        <option value="">Select Language</option>
                                        {languageOptions.other_languages.map((item) => (
                                            <option key={item.value} value={item.value}>
                                                {item.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}


                            <div className="ivr-row">
                                {/* STT */}
                                <div className="ivr-field">
                                    <label>
                                        <MessageSquare size={16} /> Speech to Text
                                    </label>
                                    <select
                                        value={formData.speechToText}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                speechToText: e.target.value,
                                            })
                                        }
                                    >
                                        <option value="">Select Provider</option>
                                        {ivrDropdowns?.stt_options?.map((item) => (
                                            <option key={item.value} value={item.value}>
                                                {item.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>


                                {/* TTS */}
                                <div className="ivr-field">
                                    <label>
                                        <Mic size={16} /> Text to Speech
                                    </label>
                                    <select
                                        value={formData.textToSpeech}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                textToSpeech: e.target.value,
                                            })
                                        }
                                    >
                                        <option value="">Select Provider</option>
                                        {ivrDropdowns?.tts_options?.map((item) => (
                                            <option key={item.value} value={item.value}>
                                                {item.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* LLM */}
                                <div className="ivr-field">
                                    <label>
                                        <Brain size={16} /> LLM
                                    </label>
                                    <select
                                        value={formData.llm}
                                        onChange={(e) =>
                                            setFormData({ ...formData, llm: e.target.value })
                                        }
                                    >
                                        <option value="">Select LLM</option>
                                        {ivrDropdowns?.llm_options?.map((item) => (
                                            <option key={item.value} value={item.value}>
                                                {item.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* SCRIPT + STREAMING */}
                            <div className="ivr-row">
                                {/* SCRIPT FIELD */}
                                <div className="ivr-field">
                                    <label>
                                        <FileText size={16} /> Script
                                    </label>
                                    <select
                                        value={formData.script}
                                        onChange={(e) =>
                                            setFormData({ ...formData, script: e.target.value })
                                        }
                                    >
                                        <option value="">Select Script</option>
                                        {ivrDropdowns?.script_options?.map((item) => (
                                            <option key={item.value} value={item.value}>
                                                {item.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* ✅ STREAMING RADIO BUTTON */}
                                <div className="ivr-field">
                                    <label> <Radio size={22} strokeWidth={2} />Streaming</label>

                                    <div className="ivr-radio-group">
                                        <label className="ivr-radio">
                                            <input
                                                type="radio"
                                                name="streaming"
                                                value="true"
                                                checked={formData.streaming === "true"}
                                                onChange={() => setFormData({ ...formData, streaming: "true" })}
                                            />
                                            True
                                        </label>

                                        <label className="ivr-radio">
                                            <input
                                                type="radio"
                                                name="streaming"
                                                value="false"
                                                defaultValue={"false"}
                                                checked={formData.streaming === "false"}
                                                onChange={() => setFormData({ ...formData, streaming: "false" })}
                                            />
                                            False
                                        </label>
                                    </div>
                                </div>
                            </div>



                            {/* CUSTOMER DETAILS */}
                            <div className="ivr-row">
                                <div className="ivr-field">
                                    <label>
                                        <User size={16} /> Customer Name
                                    </label>

                                    <input
                                        type="text"
                                        placeholder="Enter name"
                                        value={formData.customerName}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                customerName: e.target.value,
                                            })
                                        }
                                    />
                                </div>

                                <div className="ivr-field">
                                    <label>
                                        <Phone size={16} /> Customer Number
                                    </label>
                                    <input
                                        type="tel"
                                        placeholder="Enter number"
                                        value={formData.customerNumber}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                customerNumber: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                            </div>
                            {/* CALL TRANSFER */}
                            <div className="ivr-field">
                                <label className="ivr-checkbox-label">
                                    <ToggleSwitch
                                        checked={formData.call_transfer}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                call_transfer: e.target.checked,
                                                call_transfer_number: e.target.checked
                                                    ? formData.call_transfer_number
                                                    : "",
                                            })
                                        }
                                    />
                                    Enable Call Transfer
                                </label>

                                {formData.call_transfer && (
                                    <div className="ivr-transfer-box">
                                        <div className="ivr-field">
                                            <label>
                                                <PhoneCall size={16} /> Call Transfer Number
                                            </label>

                                            <input
                                                type="tel"
                                                placeholder="Enter transfer number"
                                                value={formData.call_transfer_number || ""}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        call_transfer_number: e.target.value,
                                                    })
                                                }
                                            />
                                        </div>

                                        <div className="ivr-field">
                                            <label>
                                                <Clock size={16} />  Call Transfer Threshold
                                            </label>

                                            <input
                                                type="text"
                                                value={formData.call_transfer_threshold ?? 0}
                                                
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        call_transfer_threshold: e.target.value,
                                                    })
                                                }
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* BUTTONS */}
                            <div className="ivr-buttons">
                                <button
                                    type="submit"
                                    disabled={callStatus !== "idle" && callStatus !== "ended"}
                                    className="ivr-btn ivr-btn-primary"
                                >
                                    <PhoneCall size={16} /> Initialize Call
                                </button>
                                <button
                                    type="button"
                                    className="ivr-btn ivr-btn-secondary"
                                    onClick={handleReset}
                                >
                                    Reset
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* RIGHT PANEL */}
                <div className="ivr-right">
                    {statusDisplay && callStatus !== "idle" ? (
                        <div className="ivr-status-card">
                            <div
                                className="ivr-status-icon"
                                style={{ backgroundColor: statusDisplay.color }}
                            >
                                <statusDisplay.icon size={40} color="white" />
                            </div>
                            <h3>{statusDisplay.text}</h3>

                            <div className="ivr-customer-info">
                                <User size={16} />
                                <div>
                                    <p className="ivr-customer-name">
                                        {formData.customerName || "Unknown"}
                                    </p>
                                    <p className="ivr-customer-number">
                                        {formData.customerNumber || "No Number"}
                                    </p>
                                </div>
                            </div>

                            {callStatus === "connected" && (
                                <>
                                    <div className="ivr-timer">
                                        <Clock size={18} />
                                        <span>{formatDuration(duration)}</span>
                                    </div>

                                    <div className="ivr-end-call-container">
                                        <button className="ivr-end-call-btn" onClick={handleEndCall}>
                                            <PhoneOff size={18} /> End Call
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="ivr-info-card">
                            <h3>Quick Info</h3>
                            <ul>
                                <li>
                                    <Brain size={16} /> AI-Powered Conversation
                                </li>
                                <li>
                                    <MessageSquare size={16} /> Real-time Transcription
                                </li>
                                <li>
                                    <Mic size={16} /> Natural Voice Output
                                </li>
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
